/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import shaka from "shaka-player";

import { AdsEventManager } from "../../types";
import { AdTracker, CurrentAdTrackerShaka } from "../CurrentAdTracker";
import { PlayerDimension } from "../types";

export class EventManagerShaka implements AdsEventManager {
  protected player: shaka.Player;
  protected manager: google.ima.AdsManager;
  protected currentAdTracker: CurrentAdTrackerShaka;
  protected readonly startMuted: boolean;
  protected adStarted = false;
  protected adStartedWatchHandler?: number = undefined;

  constructor(
    manager: google.ima.AdsManager,
    player: shaka.Player,
    startMuted: boolean,
  ) {
    this.manager = manager;
    this.player = player;
    this.startMuted = startMuted;

    this.currentAdTracker = new CurrentAdTrackerShaka(manager, player);
    this.createEventListeners();
  }

  get adTracker(): AdTracker {
    return this.currentAdTracker.data;
  }

  get cuePoints(): number[] {
    return this.manager.getCuePoints();
  }

  volume(value: number) {
    this.manager.setVolume(value);
    this.currentAdTracker.muted(value === 0);
  }

  muted(state: boolean) {
    const currentVolume = this.manager.getVolume();

    // do not reset volume level when not muted
    if (!state && currentVolume !== 0) {
      return;
    }

    this.manager.setVolume(state ? 0 : 1);
    this.currentAdTracker.muted(state);
  }

  pause() {
    this.manager.pause();
    this.currentAdTracker.pause();
  }

  getVolume() {
    return this.player.getMediaElement()?.volume || 0;
  }

  triggerEvent(eventName: string) {
    this.player.getMediaElement()?.dispatchEvent(new CustomEvent(eventName));
  }

  pauseContent() {
    this.player.getMediaElement()?.pause();
  }

  setContentMuted(shouldMute: boolean): void {
    const videoEl = this.player.getMediaElement();
    if (!videoEl) {
      return;
    }
    videoEl.muted = shouldMute;
  }

  protected startAd() {}

  resume() {
    this.manager.resume();
    this.currentAdTracker.resume();
  }

  skip() {
    this.manager.skip();
  }

  initialize = (dimension: PlayerDimension, onInit: () => void) => {
    try {
      this.manager.init(
        dimension.width,
        dimension.height,
        google.ima.ViewMode.NORMAL,
      );

      onInit();

      this.manager.setVolume(this.getVolume());
      this.manager.start();
    } catch (adError) {
      this.onAdError(adError as google.ima.AdErrorEvent);
    }
  };

  dispose = () => {
    window.clearInterval(this.adStartedWatchHandler);

    this.manager.stop();
    this.currentAdTracker.stop();
    this.manager.destroy();
  };

  onPlayerResize = (width: number, height: number) => {
    this.manager.resize(width, height, google.ima.ViewMode.NORMAL);
  };

  onEnterFullScreen = (): void => {
    this.manager.resize(
      // iPadOS with Safari does not handle well window.screen
      // (width and height) with screen orientation, inner width and height
      // on window object is correctly handled
      window.innerWidth,
      window.innerHeight,
      google.ima.ViewMode.FULLSCREEN,
    );
  };

  onLeaveFullScreen = (width: number, height: number): void => {
    this.manager.resize(width, height, google.ima.ViewMode.NORMAL);
  };

  contentComplete = () => {
    const noPostroll =
      this.manager.getCuePoints() && !this.manager.getCuePoints().includes(-1);

    if (noPostroll) {
      this.triggerEvent("nopostroll");
    }
  };

  protected createEventListeners = () => {
    this.manager.addEventListener(
      google.ima.AdErrorEvent.Type.AD_ERROR,
      this.onAdError,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.LOADED,
      this.onAdDataLoaded,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      this.onContentPauseRequested,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.onContentResumeRequested,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
      this.onAllAdsCompleted,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.STARTED,
      this.onAdStarted,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.COMPLETE,
      this.onAdComplete,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED,
      this.onSkipStateChange,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.SKIPPED,
      this.onAdComplete,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.PAUSED,
      this.onAdPaused,
    );

    this.manager.addEventListener(
      google.ima.AdEvent.Type.RESUMED,
      this.onAdResumed,
    );

    this.manager.addEventListener(google.ima.AdEvent.Type.LOG, this.onAdLog);
  };

  protected onAdError = (event: google.ima.AdErrorEvent) => {
    console.error("Unable to load or process ad:", event);
    this.dispose();
  };
  protected onAdDataLoaded = () => {
    this.triggerEvent("adloaded");
  };

  protected endAd() {
    this.manager.stop();
  }

  protected skipAd() {
    this.manager.skip();
  }

  protected onContentPauseRequested = () => {
    this.pauseContent();
    this.adStarted = false;

    if (this.startMuted) {
      this.setContentMuted(true);
      this.muted(true);
    }

    this.triggerEvent("adloading");
    //this.startAd();

    // if advertisement does not start after timeout, leave linear mode
    // to resume content
    this.adStartedWatchHandler = window.setTimeout(() => {
      if (!this.adStarted) {
        this.endAd();
      }
    }, 5000);
  };

  protected onContentResumeRequested = () => {
    if (this.startMuted) {
      this.setContentMuted(false);
      this.muted(false);
    }

    this.triggerEvent("adlinearmodeend");
    this.endAd();
  };

  protected onAdStarted = (event: google.ima.AdEvent) => {
    this.adStarted = true;
    const currentAd = event.getAd();

    if (!currentAd?.isLinear()) {
      this.skipAd();
    }

    if (currentAd) {
      this.triggerEvent("adstarted");
      this.currentAdTracker.start(currentAd);
    }
  };

  protected onAdComplete = () => {
    this.currentAdTracker.stop();
  };

  protected onAdPaused = () => {
    this.triggerEvent("adpaused");
  };

  protected onAdResumed = () => {
    this.triggerEvent("adresumed");
  };

  protected onSkipStateChange = () => {
    if (this.manager.getAdSkippableState()) {
      this.triggerEvent("adskipallowed");
    }
  };

  protected onAllAdsCompleted = () => {
    this.triggerEvent("adallcompleted");
  };

  protected onAdLog = (event: google.ima.AdEvent) => {
    console.log("Unable to play ad", event.getAdData());
  };
}
